/**
 * Review cards slider (blocks/_product-reviews.liquid).
 * Judge.me renders its list after load and replaces it when paging, so the
 * track is looked up on demand and watched; the arrows scroll one card and
 * switch off at either end. "Bewertung schreiben" in the summary column opens
 * Judge.me's own form (its button is hidden in the card layout).
 */
class ReviewSlider extends HTMLElement {
  connectedCallback() {
    this.arrows = [...this.querySelectorAll('.pr-slider__arrow')];
    this.onArrow = (event) => this.scrollByCard(Number(event.currentTarget.dataset.dir));
    this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrow));

    this.writeButton = this.closest('.pr-layout')?.querySelector('[data-review-write]');
    this.onWrite = () => this.openForm();
    this.writeButton?.addEventListener('click', this.onWrite);

    this.onScroll = () => this.update();
    this.observer = new MutationObserver(() => this.bind());
    this.observer.observe(this, { childList: true, subtree: true });
    this.resize = new ResizeObserver(() => this.update());
    this.resize.observe(this);
    this.bind();
  }

  disconnectedCallback() {
    this.arrows.forEach((arrow) => arrow.removeEventListener('click', this.onArrow));
    this.writeButton?.removeEventListener('click', this.onWrite);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    this.resize?.disconnect();
  }

  bind() {
    const track = this.querySelector('.jdgm-rev-widg__reviews');
    if (track !== this.track) {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track = track;
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.track) this.track.scrollLeft = 0;
    }
    this.update();
  }

  /** @param {number} dir */
  scrollByCard(dir) {
    if (!this.track) return;
    const card = this.track.querySelector('.jdgm-rev');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
    const step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
    this.track.scrollBy({ left: dir * step, behavior: 'smooth' });
  }

  update() {
    const t = this.track;
    const scrollable = !!t && t.scrollWidth > t.clientWidth + 2;
    this.toggleAttribute('data-scrollable', scrollable);
    if (!t) return;
    const [prev, next] = this.arrows;
    if (prev) prev.disabled = t.scrollLeft <= 2;
    if (next) next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
  }

  openForm() {
    const form = this.querySelector('.jdgm-form-wrapper');
    if (!(form instanceof HTMLElement)) return;
    // Judge.me ignores clicks on its hidden button, so the form is toggled
    // here; its own submit handling works once the form is visible.
    const open = form.style.display !== 'none' && form.offsetHeight > 0;
    form.style.display = open ? 'none' : 'block';
    if (!open) requestAnimationFrame(() => form.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
}

if (!customElements.get('review-slider')) {
  customElements.define('review-slider', ReviewSlider);
}
